import { useEffect, useRef, useState } from "react";
import { RichText } from "./RichText";

/** ~380 words per minute — faster than reading speed, so it reads as
 *  responsive rather than as a wait. */
const MS_PER_WORD = 26;

const TABLE_ROW = /^\s*\|.*\|\s*$/;

/**
 * Splits a reply into the units the animation reveals one at a time.
 *
 * Words everywhere except inside a table: a half-typed table is not a table
 * yet, it is a row of loose pipe characters that reflows into a grid when the
 * last cell lands. Table blocks are therefore revealed whole.
 */
function revealChunks(text: string): string[] {
  const chunks: string[] = [];
  const lines = text.split("\n");
  let table: string[] = [];

  const flushTable = () => {
    if (table.length === 0) return;
    chunks.push(`${table.join("\n")}\n`);
    table = [];
  };

  lines.forEach((line, index) => {
    const newline = index < lines.length - 1 ? "\n" : "";
    if (TABLE_ROW.test(line)) {
      table.push(line);
      return;
    }
    flushTable();

    const words = line.match(/\S+\s*/g);
    if (!words) {
      // A blank line still has to be emitted; it is what closes a paragraph.
      chunks.push(line + newline);
      return;
    }
    words[words.length - 1] += newline;
    chunks.push(...words);
  });

  flushTable();
  return chunks;
}

/**
 * Types a reply out instead of dropping it in whole.
 *
 * Rendered through RichText at every step so formatting appears as it is
 * written rather than snapping into place at the end. Reveals whole words:
 * character-by-character makes Indonesian text jitter as long words reflow.
 */
export function TypedText({
  text,
  className,
  onTick,
  enabled = true,
}: {
  text: string;
  className?: string;
  /** Fired as the text grows so the thread can stay scrolled to the bottom. */
  onTick?: () => void;
  enabled?: boolean;
}) {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const animate = enabled && !prefersReducedMotion;
  const [shown, setShown] = useState(() => (animate ? "" : text));
  const [done, setDone] = useState(!animate);
  const tickRef = useRef(onTick);
  tickRef.current = onTick;

  useEffect(() => {
    const finish = () => {
      setShown(text);
      setDone(true);
    };

    if (!animate) {
      finish();
      return;
    }

    const chunks = revealChunks(text);
    if (chunks.length === 0) {
      finish();
      return;
    }

    // A backgrounded tab clamps timers to ~1s and stops rAF entirely, which
    // would stretch a long reply over minutes. Every answer has to arrive
    // complete, so an unseen animation is skipped rather than queued.
    if (document.hidden) {
      finish();
      return;
    }

    setShown("");
    setDone(false);

    const started = performance.now();
    let frame = 0;

    const step = () => {
      // The position comes from elapsed time, not from a tick count, so a
      // throttled tab catches up in a single frame instead of crawling.
      const elapsed = performance.now() - started;
      const index = Math.min(chunks.length, Math.floor(elapsed / MS_PER_WORD) + 1);
      setShown(chunks.slice(0, index).join(""));
      tickRef.current?.();
      if (index < chunks.length) {
        frame = requestAnimationFrame(step);
      } else {
        setDone(true);
      }
    };
    frame = requestAnimationFrame(step);

    const finishIfHidden = () => {
      if (!document.hidden) return;
      cancelAnimationFrame(frame);
      finish();
    };
    document.addEventListener("visibilitychange", finishIfHidden);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("visibilitychange", finishIfHidden);
    };
  }, [text, animate]);

  return (
    <div className={className}>
      <RichText text={shown} />
      {!done && (
        <span
          aria-hidden
          className="bg-content-muted ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 animate-pulse"
        />
      )}
      {/* The full text is exposed to assistive tech immediately; a screen
          reader should not have to wait for an animation to finish. */}
      {!done && <span className="sr-only">{text}</span>}
    </div>
  );
}
